<template>
    <div class="container" v-loading="loading">
        <div class="back" @click="goBack"><i class="el-icon-back"></i> 返回</div>
        <section v-if="info.code" style="overflow:hidden;border:1px solid #e7ebf3;border-radius:8px;background:#fff;box-shadow:0 2px 10px rgba(31,45,61,.05)">
            <div style="display:flex;justify-content:space-between;align-items:center;gap:16px;padding:12px 16px;border-bottom:1px solid #edf0f5;background:#f8fbff">
                <div style="display:flex;min-width:0;align-items:center;flex-wrap:wrap;gap:10px">
                    <strong style="color:#303133;font-size:17px;line-height:1.4">{{ info.name }}</strong>
                    <code style="overflow-wrap:anywhere;padding:2px 7px;border-radius:4px;background:#eef3f9;color:#606266;font-family:monospace;font-size:12px">{{ info.code }}</code>
                    <span style="color:#a0a4ab;font-size:12px">基础信息创建后不可修改</span>
                </div>
                <div style="display:flex;flex-shrink:0;gap:6px">
                    <span style="display:inline-flex;align-items:center;height:24px;padding:0 9px;border:1px solid #c6e2ff;border-radius:12px;background:#ecf5ff;color:#409eff;font-size:12px">
                        {{ packageTypeText(info.package_type) }}
                    </span>
                    <span :style="statusBadgeStyle(info.status)">{{ statusText(info.status) }}</span>
                </div>
            </div>

            <div style="display:flex;align-items:center;flex-wrap:wrap;padding:10px 16px;gap:0">
                <div v-for="(item, index) in overviewItems" :key="item.label"
                    :style="{display:'flex',alignItems:'center',gap:'6px',padding:'2px 16px 2px 0',marginRight:'16px',borderRight:index === overviewItems.length - 1 ? '0' : '1px solid #e7ebf3'}">
                    <span style="color:#909399;font-size:12px">{{ item.label }}</span>
                    <strong style="color:#303133;font-size:14px;font-weight:600;white-space:nowrap">{{ item.value }}</strong>
                </div>
                <div v-if="info.explain" style="min-width:220px;flex:1;color:#606266;font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" :title="info.explain">
                    <span style="margin-right:6px;color:#909399">说明</span>{{ info.explain }}
                </div>
            </div>
        </section>

        <el-tabs v-if="info.code" v-model="activeTab" class="management-tabs">
            <el-tab-pane label="价格管理" name="price">
                <div v-if="info.is_trial === 1 && prices.length === 0" class="empty-tip">免费试用套餐暂无价格。</div>
                <div v-else>
                    <div v-for="(item, index) in prices" :key="index" class="price-row">
                        <div style="display:flex;width:200px;flex-direction:column;gap:5px">
                            <span style="color:#606266;font-size:12px">币种</span>
                            <el-select v-model="item.currency" filterable placeholder="请选择币种">
                                <el-option v-for="currency in currencyList" :key="currency.code" :label="currency.name" :value="currency.code"></el-option>
                            </el-select>
                        </div>
                        <div style="display:flex;width:180px;flex-direction:column;gap:5px">
                            <span style="color:#606266;font-size:12px">原价</span>
                            <el-input-number v-model="item.price" :min="0" :precision="2" placeholder="请输入原价"></el-input-number>
                        </div>
                        <div style="display:flex;width:180px;flex-direction:column;gap:5px">
                            <span style="color:#606266;font-size:12px">售价</span>
                            <el-input-number v-model="item.sale_price" :min="0" :precision="2" placeholder="请输入售价"></el-input-number>
                        </div>
                        <el-button style="margin-top:22px" type="danger" size="mini" @click="removePrice(index)">删除</el-button>
                    </div>
                    <el-button type="primary" plain size="small" @click="addPrice">添加币种价格</el-button>
                    <el-button type="primary" size="small" :loading="priceSaving" @click="savePrices">保存完整价格列表</el-button>
                    <div class="help-text">保存采用全量覆盖：列表中删除的币种也会从服务端删除。</div>
                </div>
            </el-tab-pane>

            <el-tab-pane label="上架区域" name="region">
                <el-form label-width="90px" size="small" style="max-width:620px">
                    <el-form-item label="上架区域">
                        <el-select v-model="regions" multiple filterable allow-create default-first-option
                            placeholder="请选择或输入区域码" style="width:420px">
                            <el-option label="US" value="US"></el-option>
                            <el-option label="EU" value="EU"></el-option>
                            <el-option label="CN" value="CN"></el-option>
                        </el-select>
                        <div class="help-text">可多选区域码；清空后提交空数组，表示暂不指定区域。</div>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" :loading="regionSaving" @click="saveRegions">保存上架区域</el-button>
                    </el-form-item>
                </el-form>
            </el-tab-pane>

            <el-tab-pane label="功能特性" name="feature">
                <div v-if="!extFeatures.length" class="empty-tip">该套餐暂无功能特性。</div>
                <el-table v-else border :data="extFeatures" size="small">
                    <el-table-column prop="item_key" label="功能项" min-width="180"></el-table-column>
                    <el-table-column prop="item_value" label="功能值/描述" min-width="280"></el-table-column>
                    <el-table-column prop="sort_order" label="排序" width="100"></el-table-column>
                </el-table>
            </el-tab-pane>

            <el-tab-pane label="型号关联" name="model">
                <el-form :inline="true" size="small">
                    <el-form-item label="产品型号">
                        <SearchInnerModel v-model="newModelCode" style="width: 360px"></SearchInnerModel>
                    </el-form-item>
                    <el-form-item>
                        <el-button type="primary" :loading="modelSaving" @click="addModel">新增关联</el-button>
                    </el-form-item>
                </el-form>
                <el-table border :data="models" size="small">
                    <el-table-column prop="product_code" label="产品编号"></el-table-column>
                    <el-table-column prop="product_model_code" label="产品型号编号"></el-table-column>
                    <el-table-column label="操作" width="100">
                        <template v-slot="scope">
                            <el-button type="danger" size="mini" @click="deleteModel(scope.row)">解除关联</el-button>
                        </template>
                    </el-table-column>
                </el-table>
                <el-pagination v-model:currentPage="modelPage" :page-size="modelLimit" :total="modelTotal"
                    layout="total, prev, pager, next" @current-change="loadModels"></el-pagination>
            </el-tab-pane>
        </el-tabs>
    </div>
</template>

<script>
import api from '../../../axios/pack'
import commonApi from '../../../axios/api'
import SearchInnerModel from '../../../components/device/SearchInnerModel.vue'

export default {
    name: 'HuntingPackageDetail',
    components: {
        SearchInnerModel
    },
    data() {
        return {
            packageCode: '',
            info: {},
            prices: [],
            extFeatures: [],
            models: [],
            currencyList: [],
            activeTab: 'price',
            loading: false,
            priceSaving: false,
            modelSaving: false,
            regionSaving: false,
            regions: [],
            newModelCode: '',
            modelPage: 1,
            modelLimit: 20,
            modelTotal: 0
        }
    },
    computed: {
        overviewItems() {
            return [
                { label: '时长', value: this.info.time + ' ' + this.info.time_unit },
                { label: '保留', value: this.info.file_time + ' 天' },
                { label: '试用', value: this.info.is_trial === 1 ? this.info.trial_days + ' 天' : '否' },
                { label: '标签', value: this.tagText(this.info.tag) },
                { label: '排序', value: this.info.sort_order }
            ]
        }
    },
    methods: {
        packageTypeText(value) { return value === 1 ? '通信+云存储' : value === 2 ? '纯云存储' : '-' },
        tagText(value) { return value === 'recommended' ? '推荐' : value === 'best_value' ? '最划算' : '无标签' },
        statusText(value) { return ['测试', '上架', '下架', '已删除'][value] || '-' },
        statusBadgeStyle(status) {
            const colors = {
                0: ['#ecf5ff', '#409eff', '#c6e2ff'],
                1: ['#f0f9eb', '#67c23a', '#e1f3d8'],
                2: ['#fdf6ec', '#e6a23c', '#faecd8'],
                3: ['#fef0f0', '#f56c6c', '#fde2e2']
            }
            const color = colors[status] || colors[0]
            return {
                display: 'inline-flex',
                alignItems: 'center',
                height: '24px',
                padding: '0 9px',
                border: '1px solid ' + color[2],
                borderRadius: '12px',
                background: color[0],
                color: color[1],
                fontSize: '12px'
            }
        },
        loadCurrencies() {
            commonApi.CurrencyList({ page: 1, limit: 200 }).then(res => {
                if (res.data.code == 200) this.currencyList = res.data.data.data || []
            })
        },
        loadDetail() {
            this.loading = true
            api.HuntingPackageDetail({ code: this.packageCode }).then(res => {
                if (res.data.code == 200) {
                    this.info = res.data.data.info || {}
                    this.prices = (res.data.data.prices || []).map(item => ({
                        currency: item.currency,
                        price: Number(item.price),
                        sale_price: Number(item.sale_price)
                    }))
                    this.extFeatures = Array.isArray(res.data.data.ext_features) ? res.data.data.ext_features : []
                    return
                }
                this.$message.error('获取套餐详情失败：' + res.data.msg)
            }).finally(() => { this.loading = false })
        },
        addPrice() { this.prices.push({ currency: '', price: null, sale_price: null }) },
        removePrice(index) { this.prices.splice(index, 1) },
        validatePrices() {
            if (!this.prices.length) return '价格列表不能为空'
            if (this.prices.some(item => !item.currency)) return '请选择币种'
            if (new Set(this.prices.map(item => item.currency)).size !== this.prices.length) return '币种不可重复'
            if (this.prices.some(item => item.price === null || item.price === undefined || item.sale_price === null || item.sale_price === undefined)) return '请填写原价和售价'
            return ''
        },
        savePrices() {
            const message = this.validatePrices()
            if (message) return this.$message.error(message)
            this.priceSaving = true
            api.HuntingPackageUpdatePrice({
                package_code: this.packageCode,
                prices: this.prices.map(item => ({ currency: item.currency, price: Number(item.price), sale_price: Number(item.sale_price) }))
            }).then(res => {
                if (res.data.code == 200) {
                    this.$message.success('价格保存成功')
                    this.loadDetail()
                    return
                }
                this.$message.error('价格保存失败：' + res.data.msg)
            }).finally(() => { this.priceSaving = false })
        },
        loadRegions() {
            api.HuntingPackageRegions({ code: this.packageCode }).then(res => {
                if (res.data.code == 200) {
                    this.regions = Array.isArray(res.data.data) ? res.data.data : []
                    return
                }
                this.$message.error('获取上架区域失败：' + res.data.msg)
            })
        },
        saveRegions() {
            this.regionSaving = true
            api.HuntingPackageUpdateRegion({ code: this.packageCode, regions: this.regions }).then(res => {
                if (res.data.code == 200) {
                    this.$message.success('上架区域保存成功')
                    return
                }
                this.$message.error('上架区域保存失败：' + res.data.msg)
            }).finally(() => { this.regionSaving = false })
        },
        loadModels() {
            api.HuntingPackageModelList({ set_meal_code: this.packageCode, page: this.modelPage, limit: this.modelLimit }).then(res => {
                if (res.data.code == 200) {
                    this.models = res.data.data.data || []
                    this.modelTotal = res.data.data.page_info ? res.data.data.page_info.total : 0
                    return
                }
                this.$message.error('获取型号关联失败：' + res.data.msg)
            })
        },
        addModel() {
            if (!this.newModelCode) return this.$message.error('请输入产品型号编号')
            this.modelSaving = true
            api.HuntingPackageModelCreate({ set_meal_code: this.packageCode, product_model_code: this.newModelCode }).then(res => {
                if (res.data.code == 200) {
                    this.newModelCode = ''
                    this.$message.success('型号关联成功')
                    this.modelPage = 1
                    this.loadModels()
                    return
                }
                this.$message.error('型号关联失败：' + res.data.msg)
            }).finally(() => { this.modelSaving = false })
        },
        deleteModel(row) {
            this.$confirm('确定解除该型号关联?', '提示', { type: 'warning' }).then(() => {
                api.HuntingPackageModelDelete({ id: row.id }).then(res => {
                    if (res.data.code == 200) {
                        this.$message.success('已解除关联')
                        this.loadModels()
                        return
                    }
                    this.$message.error('解除关联失败：' + res.data.msg)
                })
            }).catch(() => {})
        },
        goBack() { this.$router.push('/iot/huntingpack') }
    },
    created() {
        this.packageCode = this.$route.params.code
        this.loadCurrencies()
        this.loadDetail()
        this.loadRegions()
        this.loadModels()
    }
}
</script>

<style scoped>
.back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 16px;
    color: #606266;
    cursor: pointer;
    font-size: 14px;
}

.back:hover {
    color: #409eff;
}

.management-tabs {
    margin-top: 24px;
}

.price-row {
    display: flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 10px;
}

.price-row .el-select {
    width: 250px;
}

.status-select {
    width: 100px !important;
}

.help-text,
.empty-tip {
    margin-top: 10px;
    color: #909399;
    font-size: 12px;
}
</style>
